import { vec2 } from "@tlaukkan/tsm";
import { Entity, Not, System } from "ecsy";
import { calculateTargetLead, queryRadius } from "../box2dUtils";
import { AimForPlayer, Consumer, Cooldown, Dead, EffectRadius, ElementInfo, HasSplit, Infected, LaneInfo, Ownership, ScatterProtein, Spawner, SpawnOnce, SpawnSpec, ReplaceSelf, Submerged, Sync, Transform, Virus, VisibleRange } from "../components";
import { CompositionElement, getEntityPlayer } from "../composition";
import { Speeds } from "../constants";
import { createEntity } from "../entityFactory";
import { Game } from "../game";
import { GameElement } from "../gameElementTypes";
import { addComponent, angleBetween, angleFromUp, degToRad, getSystem, perpendicular, radToDeg, rotateAround } from "../utils";
import { LifeAndDeathSystem } from "./lifeAndDeathSystem";

export class SpawnSystem extends System {
	private game: Game;
	lifeAndDeathSystem!: LifeAndDeathSystem;
	private ranLastFrame = false;

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
		this.lifeAndDeathSystem = getSystem(this.world, LifeAndDeathSystem)
	}

	execute(delta: number) {
		if (!this.game.inSimulation) {
			if (this.ranLastFrame) {
				//Still need to clean these up
				this.clearSpawningNow();
				this.ranLastFrame = false;
			}
			return;
		}
		this.ranLastFrame = true;

		//Split means spawn new objects and destroy yourself
		this.queries.replacers.results.forEach(entity => {
			const replacer = entity.getMutableComponent(ReplaceSelf);
			const transform = entity.getComponent(Transform);
			const entityInfo = entity.getComponent(ElementInfo);

			let type: GameElement | null = null;
			let copyFrom: Entity | null = null;
			let copySourceCompElement: CompositionElement | undefined;

			if (replacer.spawnTypeSource == "nearest") {
				const range = entity.getComponent(VisibleRange);
				const nearest = range && range.nearest;
				if (nearest) {
					copyFrom = nearest;
				}
			}
			else if (replacer.spawnTypeSource == "self") {
				type = entityInfo?.type ?? null; //not ||, SIMPLE is 0
			}
			else if (replacer.spawnTypeSource === "specified") {
				type = replacer.spawnType;
			}

			if (copyFrom != null) {
				const info = copyFrom.getComponent(ElementInfo);
				copySourceCompElement = this.game.getCompElementById(info.id) || undefined;
				type = info.type;
			}
			if (type === null) {
				return;
			}

			const ownder = entity.getComponent(Ownership);
			const playerId = ownder ? ownder.playerId : -1;
			//Viruses aim at the player in spawningPlayerId, so children must inherit it or they never pick a target
			const aimForPlayer = entity.getComponent(AimForPlayer);
			const spawningPlayerId = aimForPlayer ? aimForPlayer.playerId : playerId;
			addComponent(entity, HasSplit);

			for (let i = 0; i < replacer.numSplits; i++) {
				const headingOffset = (i - (replacer.numSplits - 1) / 2) * replacer.spreadRadians;
				const heading = rotateAround(transform.forward, headingOffset, vec2.zero);
				const perpendicularVec = perpendicular(transform.forward.copy())
					.scale(transform.size.x * (i))
					.add(transform.position.copy());
				createEntity({
					type,
					position: perpendicularVec.xy,
					rotation: transform.rotation + headingOffset,
					playerId,
					unsynced: false,
					entityParameters: {
						initialPush: heading.scale(replacer.initialPush).xy,
						copySourceCompElement,
						spawningPlayerId,
						isSplitChild: true,
						creatorSyncId: entityInfo?.id || undefined
					}
				});
			}
		});

		//Spawners just spawn new objects
		for (const entity of this.queries.spawners.results) {
			const spawner = entity.getComponent(Spawner);
			const cooldown = entity.getComponent(Cooldown);

			const spawn = this.shouldSpawn(spawner, cooldown);
			if (spawn === null) continue;

			const owned = entity.getComponent(Ownership);
			const playerId = owned ? owned.playerId : -1;

			entity.getMutableComponent(Spawner); //signal change
			this.spawn(entity, spawner.spawns, playerId);
		}

		for (const entity of this.queries.oneTime.results) {
			const spawner = entity.getComponent(SpawnOnce, false);
			const owned = entity.getComponent(Ownership);
			const playerId = owned ? owned.playerId : -1;

			this.spawn(entity, spawner.spawns, playerId);
		}

		for (const entity of this.queries.infected.results) {
			const spawner = entity.getComponent(Spawner);
			const transform = entity.getComponent(Transform);
			const cooldown = entity.getComponent(Cooldown);
			const infected = entity.getComponent(Infected);
			const owningPlayer = getEntityPlayer(entity, this.game);

			const spawn = this.shouldSpawn(spawner, cooldown)
			if (spawn === null) continue;
			if (!owningPlayer) continue;

			entity.getMutableComponent(Spawner); //signal change

			const offsetPosition = transform.position.copy();

			const targetPos = new vec2(owningPlayer.spawnPoint) //use blood cell center point!
			const lineToOwner = vec2.direction(targetPos, transform.position);
			const toOwningPlayer = angleBetween(lineToOwner, transform.forward);
			const lane = entity.getComponent(LaneInfo);
			const spawnLane = lane ? lane.lane : undefined;

			for (const playerId of infected.targetPlayerIds) {
				//Find offset position and rotation for each player direction
				const targetPlayer = this.game.players.find(player => player.id === playerId)!;
				const targetPos = new vec2(targetPlayer.spawnPoint)
				const lineToTarget = vec2.direction(targetPos, transform.position);
				const angle = angleBetween(lineToTarget, transform.forward);
				const toTargetRotation = toOwningPlayer - angle;

				spawner.spawns.forEach(spec => {
					if (!spec.params) spec.params = {};
					spec.params.spawningPlayerId = playerId; //player !== null ? player.id : -1;
					spec.initialPush = Speeds.WeakImpulse;
					spec.headingRotation = toTargetRotation;
				});
				this.spawn(entity, spawner.spawns, playerId, offsetPosition, transform.rotation, spawnLane);
			}

			if (spawner.amount <= 0) {
				addComponent(entity, Submerged, { dontAffectScale: true });
			}
		}

		this.queries.scatterProtein.added?.forEach(entity => {
			const scatter = entity.getComponent(ScatterProtein);
			let amount = scatter.fixedAmount;
			if (amount <= 0 && entity.hasComponent(Consumer)) {
				const consumer = entity.getComponent(Consumer);
				amount = consumer.consumedAmounts[scatter.type];
			}

			if (amount <= 0) return;

			const transform = entity.getComponent(Transform);
			const info = entity.getComponent(ElementInfo);
			const sync = entity.getComponent(Sync);
			const player = getEntityPlayer(entity, this.game);
			const playerId = player !== null ? player.id : -1;
			const specs: SpawnSpec[] = [];

			//If target viruses first aim at the viruses within range. TODO: Should we limit range to a cone?
			if (scatter.targetViruses) {
				const virusInRange = queryRadius(transform.position, scatter.range, (e) => e.hasComponent(Virus));
				for (const virus of virusInRange) {
					if (amount <= 0) continue;
					//Send one bullet after each virus while we have enough bullets
					const targetPosition = calculateTargetLead(virus, transform.position);
					const directionToTarget = vec2.direction(transform.position, targetPosition)
					const angle = angleFromUp(directionToTarget);
					specs.push({
						type: GameElement.BULLET,
						rotation: angle,
						params: {
							targetPosition: targetPosition.xy, //not used, but left in in case
							targetDirection: directionToTarget.xy,
						}
					});
					--amount;
				}
			}

			//Spread evenly around around the heading so divide by 2
			const spread = scatter.spreadRadiens / 2;
			for (let i = 0; i < amount; i++) {
				const random = (sync.rng.next() - 0.5) * 2;
				specs.push({
					type: GameElement.BULLET,
					initialPush: Speeds.FastImpulse,
					headingRotation: random * spread,
				});
			}
			this.spawn(entity, specs, playerId, transform.position, transform.rotation);
		});
	}

	private clearSpawningNow() {
		for (const query of [this.queries.spawners, this.queries.infected]) {
			for (const entity of query.results) {
				entity.getComponent(Spawner).spawningNow = false;
			}
		}
	}

	shouldSpawn(spawner: Spawner, cooldown: Cooldown | null) {
		spawner.spawningNow = false;

		if (spawner.amount <= 0) return null;

		if (cooldown) {
			if (!cooldown.done) {
				return null;
			}
			cooldown.restart();
		}

		spawner.spawningNow = true;
		--spawner.amount;
		return spawner.spawns;
	}

	spawn(entity: Entity, specs: SpawnSpec[], playerId = -1, position: vec2 | null = null, rotation = 0, spawnLane?: number) {
		const transform = entity.getComponent(Transform);
		if (!transform) return;

		const sync = entity.getComponent(Sync);
		const effect = entity.getComponent(EffectRadius);

		for (const spec of specs) {
			if (!spec.params) spec.params = {};

			const push = spec.initialPush !== undefined ? spec.initialPush : 0;
			if (push > 0) {
				const direction = transform.forward.copy();
				if (spec.headingRotation !== undefined) {
					rotateAround(direction, spec.headingRotation, vec2.zero, direction);
				}
				spec.params.initialPush = direction.scale(push).xy;
			}

			let calculatedPosition = position !== null ? position.xy : transform.position.xy;
			if (spec.offset) {
				const position = spec.offset.copy().scale(transform.size.x).add(transform.position).copy();
				rotateAround(position, transform.rotation, transform.position, position);
				calculatedPosition = position.xy;
			}

			if (transform) {
				createEntity({
					type: spec.type,
					promoted: spec.promoted,
					position: calculatedPosition,
					playerId,
					rotation,
					unsynced: !sync,
					entityParameters: {
						...spec.params,
						effectRadius: effect ? effect.radius : undefined,
						creatorSyncId: sync ? sync.id : undefined,
						spawnLane
					}
				});
			}


			if (spec.type < GameElement.SHOPABLE_ELEMENT_NUMBER) {
				this.game.incrementRoundStat(playerId, 'immuneCellsSpawnedInCombat', 1);
			}
		}
	}
}

SpawnSystem.queries = {
	oneTime: { components: [SpawnOnce] },
	spawners: { components: [Spawner, Not(Infected)] },
	infected: { components: [Spawner, Infected] },
	replacers: { components: [ReplaceSelf, Transform, ElementInfo, Not(HasSplit), Not(Dead)] },
	scatterProtein: { components: [ScatterProtein, Transform], listen: { added: true } }
}
